import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import {
    type CreateJudicialDepositPayload,
    type JudicialDeposit,
    type JudicialDepositSummary,
    type UpdateJudicialDepositPayload,
} from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';

// Valor do formulário de criação de depósito (o valor bloqueado é o único obrigatório).
interface AddJudicialDepositValue extends CreateJudicialDepositPayload {}

const EMPTY_SUMMARY: JudicialDepositSummary = {
    totalBlocked: 0,
    totalBlockedFormatted: null,
    totalWithdrawn: 0,
    totalWithdrawnFormatted: null,
    blockedNotWithdrawn: 0,
    blockedNotWithdrawnFormatted: null,
};

// Gerencia os depósitos judiciais de um caso: lista (com o agregado bloqueado/levantado),
// adiciona, edita campos (registrar o levantamento) e remove. Fetch próprio pelo id do caso
// (a seção atualiza sem recarregar o page-view inteiro). Mesmo padrão do useLegalCaseAgreements
// (seededCaseRef re-seed por caso, guarda de corrida por geração), sem status/superseção.
export default function useLegalCaseDeposits(
    caseId: string | null,
    initialDeposits?: JudicialDeposit[],
    initialSummary?: JudicialDepositSummary,
) {
    const [deposits, setDeposits] = useState<JudicialDeposit[]>(initialDeposits || []);
    const [summary, setSummary] = useState<JudicialDepositSummary>(
        initialSummary || EMPTY_SUMMARY,
    );
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);
    const seededCaseRef = useRef<string | null>(null);
    // Geração de request: guarda contra resposta obsoleta. O fetch do mount pode chegar depois
    // de uma mutação e sobrescrever a lista com o snapshot antigo — o mesmo "sumiço" que o
    // re-fetch veio corrigir, agora por corrida. Cada fetch captura a geração ao iniciar e só
    // aplica o resultado se ainda for a corrente; toda mutação incrementa a geração.
    const fetchGenRef = useRef(0);

    const fetchDeposits = useCallback(async () => {
        if (!caseId) return;
        const generation = ++fetchGenRef.current;
        setIsLoading(true);
        try {
            const result = await recoveryApi.getDeposits(caseId);
            if (fetchGenRef.current !== generation) return;
            setDeposits(result.items || []);
            setSummary(result.summary || EMPTY_SUMMARY);
        } catch {
            // silencioso — a seção já mostra os depósitos que vieram no page-view
        } finally {
            if (fetchGenRef.current === generation) setIsLoading(false);
        }
    }, [caseId]);

    useEffect(() => {
        if (seededCaseRef.current === caseId) return;
        seededCaseRef.current = caseId;
        setDeposits(initialDeposits || []);
        setSummary(initialSummary || EMPTY_SUMMARY);
        setErrorFields([]);
        // Semeia com o que veio no page-view (paint imediato) e re-busca do servidor. O
        // re-fetch cobre o caso de a seção remontar (ao alternar de aba na página de detalhe):
        // sem ele, a remontagem voltaria ao snapshot do page-view — carregado antes de qualquer
        // inclusão nesta sessão — e o registro recém-criado "sumiria" até recarregar a página.
        fetchDeposits();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);

    const clearErrors = () => setErrorFields([]);

    const addDeposit = async (data: AddJudicialDepositValue): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.addDeposit(caseId, data);
            await fetchDeposits();
            toast.success('Depósito registrado');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            if (e instanceof ApiError && e.status === 404) {
                toast.error(e.data?.message || 'Caso não encontrado');
                return false;
            }
            toast.error(e?.message || 'Erro ao registrar depósito');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const updateDeposit = async (
        depositId: string,
        data: UpdateJudicialDepositPayload,
    ): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.updateDeposit(caseId, depositId, data);
            await fetchDeposits();
            toast.success('Depósito atualizado');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            toast.error(e?.data?.message || e?.message || 'Erro ao atualizar depósito');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const removeDeposit = async (depositId: string) => {
        if (!caseId) return;
        setIsSaving(true);
        try {
            await recoveryApi.removeDeposit(caseId, depositId);
            await fetchDeposits();
            toast.success('Depósito removido');
        } catch (e: any) {
            toast.error(e?.data?.message || e?.message || 'Erro ao remover depósito');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        deposits, summary, isLoading, isSaving, errorFields,
        addDeposit, updateDeposit, removeDeposit, clearErrors,
    };
}

export type { AddJudicialDepositValue };
